// src/components/ui/OrdenCompraForm.jsx
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import { toast } from "react-toastify";

import InputField from "./InputField";
import SelectField from "./SelectField";
import SelectSimple from "./SelectSimple";
import TextAreaField from "./TextAreaField";

const CondicionesPago = ["Contado", "Crédito"];

const OrdenCompraForm = ({ orden = null, onClose, onSave }) => {
  const [form, setForm] = useState({
    fecha: new Date().toISOString().split("T")[0],
    observaciones: "",
    descripcion: "",
    condicion_pago: "Contado",
    tiempo_entrega: "",
    costologistica: "",
    proveedor: "",
    clientesId: "",
    estatus: "Creada",
  });

  const [clientes, setClientes] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  // 🔹 Cargar proveedores y clientes
  const fetchBasics = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        axios.get(`${API}/clientes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/prov`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setClientes(cRes.data || []);
      setProveedores(pRes.data || []);
    } catch (err) {
      toast.error("No se pudieron cargar proveedores o clientes");
    }
  }, [API, token]);

  useEffect(() => {
    fetchBasics();
  }, [fetchBasics]);

  // 🔹 Precargar datos al editar
  useEffect(() => {
    if (orden) {
      setForm({
        fecha: orden.fecha || new Date().toISOString().split("T")[0],
        observaciones: orden.observaciones || "",
        descripcion: orden.descripcion || "",
        condicion_pago: orden.condicion_pago || "Contado",
        tiempo_entrega: orden.tiempo_entrega || "",
        costologistica: orden.costologistica || "",
        proveedor: orden.proveedor?._id || orden.proveedor || "",
        clientesId: orden.clientesId?._id || orden.clientesId || "",
        estatus: orden.estatus || "Creada",
      });
    }
  }, [orden]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const validate = () => {
    const required = [
      "condicion_pago",
      "tiempo_entrega",
      "proveedor",
      "descripcion",
    ];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Falta campo obligatorio: " + k);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const usuarioId = user?._id;

      const { estatus, ...payload } = form;
      const payloadWithUser = { ...payload, usuarioId };

      await onSave(payloadWithUser);
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Error al guardar orden",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      {/* Modal responsive con scroll interno */}
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {orden ? "Editar orden de compra" : "Nueva orden de compra"}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Descripción + Fecha */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  label="Descripción OC *"
                  name="descripcion"
                  value={form.descripcion}
                  onChange={handleChange}
                  placeholder="Agregue un nombre para la OC"
                />

                <InputField
                  label="Fecha"
                  name="fecha"
                  value={form.fecha}
                  readOnly
                />
              </div>

              {/* Proveedor + Cliente */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SelectField
                  label="Proveedor *"
                  name="proveedor"
                  value={form.proveedor}
                  options={proveedores}
                  valueKey="_id"
                  displayFn={(p) => p.name}
                  placeholder="Seleccione proveedor"
                  onChange={handleChange}
                />

                <SelectField
                  label="Cliente (Opcional)"
                  name="clientesId"
                  value={form.clientesId}
                  options={clientes}
                  valueKey="_id"
                  displayFn={(c) => c.name}
                  placeholder="Seleccione cliente"
                  onChange={handleChange}
                />
              </div>

              {/* Condición + Tiempo + Logística */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <SelectSimple
                  label="Condición de pago *"
                  name="condicion_pago"
                  value={form.condicion_pago}
                  options={CondicionesPago}
                  onChange={handleChange}
                />

                <InputField
                  label="Tiempo de entrega (días) *"
                  name="tiempo_entrega"
                  type="number"
                  value={form.tiempo_entrega}
                  onChange={handleChange}
                  placeholder="Tiempo de entrega"
                />

                <InputField
                  label="Costo logística"
                  name="costologistica"
                  type="number"
                  step="0.01"
                  value={form.costologistica}
                  onChange={handleChange}
                  placeholder="Valor de envío"
                />
              </div>

              {/* Observaciones */}
              <TextAreaField
                label="Observaciones"
                name="observaciones"
                value={form.observaciones}
                onChange={handleChange}
                placeholder="Opcional"
              />

              {/* Estatus solo lectura */}
              {orden && (
                <InputField
                  label="Estatus"
                  name="estatus"
                  value={form.estatus}
                  readOnly
                />
              )}

              {/* Acciones */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-4">
                <Button
                  type="button"
                  onClick={onClose}
                  className="bg-red-500 hover:bg-red-600 text-white w-full sm:w-auto"
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto"
                  disabled={loading}
                >
                  {loading ? "Guardando..." : orden ? "Actualizar" : "Crear"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrdenCompraForm;
